/* eslint-disable array-callback-return */
/**
 * @file EducationalMaterialLoad.jsx
 * @author Fernando Ruano
 * @description Education Material Load Page
 */

// Dependencies
import React from 'react';
import {
  Col,
  Row,
  Form,
  Select,
  Input,
  Checkbox,
  Button,
  Upload,
  Modal,
  message
} from 'antd';
import { useTranslation } from 'react-i18next';
import {
  InboxOutlined,
  UploadOutlined,
  LeftOutlined,
  CloseCircleFilled
} from '@ant-design/icons';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useLocation, useHistory } from 'react-router-dom';

// Core
import endpoints from '@nephro/config/endpoints';

// Helpers
import { educationElementType } from '@nephro/commons/helpers/utils';

const { Option } = Select;

const layout = {
  layout: 'vertical'
};

const StyledMainDiv = styled.div`
  .ant-form label {
    color: var(--color-baxter-gray) !important;
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 40px;
  font-weight: bold;
  font-size: 14px;
`;

const StyledUpload = styled.div`
  background: #f5f9ff !important;
  border: 1px solid #d9d9d9 !important;
  border-radius: 5px !important;
  padding-right: 25px;
  padding-left: 25px;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const CreateButton = styled(Button)`
  border-color: var(--color-baxter-teal-medium) !important;
  background-color: var(--color-baxter-teal-medium) !important;
  color: white;
  box-sizing: border-box;
  border-radius: 5px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: auto;
  height: 40px;
`;

const options = [
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3a', value: '3a' },
  { label: '3b', value: '3b' },
  { label: '4', value: '4' },
  { label: '5', value: '5' }
];

const { educationalMaterial: educationalElementEndpoints } = endpoints;

/**
 * EducationalMaterial Load
 * @param { Object } educationalMaterialActions - EducationalMaterial Actions.
 * @return { Node } React node.
 */

function EducationalMaterialLoad({ educationalElementActions }) {
  const { t: translate } = useTranslation();
  const [file, setFile] = React.useState(false);
  const [visible, setVisible] = React.useState(false);
  const [form] = Form.useForm();
  const location = useLocation();
  const history = useHistory();

  const { createEducationalElement } = educationalElementActions;

  const validationMessages = {
    required: translate('commons.required'),
    types: {
      email: translate('commons.emailType')
    }
  };

  const stagesValidation = (val, array) => {
    let activeStage = 1;
    if (array.indexOf(val) < 0) {
      activeStage = 0;
    }
    return activeStage;
  };

  const formatDate = (date, format) => moment(date).format(format);

  const handleUpload = (payload) => {
    const data = new FormData();
    data.append(
      'object',
      file,
      `${formatDate(moment(), 'DD-MM-YYYY_HH:mm:ss')}_${file.name}`
    );
    fetch(`${educationalElementEndpoints.s3}`, {
      method: 'POST',
      headers: {
        Accept: '*/*'
      },
      body: data
    })
      .then((res) => res.json())
      .then((res) => {
        setFile(false);
        if (res.http_status === 200) {
          message.success('Se ha cargado el archivo con éxito');

          createEducationalElement(payload, () => history.goBack());
        } else {
          message.error('Ocurrió una falla al cargar archivo');
        }
      })
      .catch(() => {
        message.error('Algo salió mal');
      });
  };

  const onFinish = (values) => {
    const data = {
      description: values.description,
      education_category: {
        ...location.state.category
      },
      name: values.title,
      stage1: stagesValidation('1', values.stages),
      stage2: stagesValidation('2', values.stages),
      stage3a: stagesValidation('3a', values.stages),
      stage3b: stagesValidation('3b', values.stages),
      stage4: stagesValidation('4', values.stages),
      stage5: stagesValidation('5', values.stages),
      type: values.elementType,
      uri: `${formatDate(moment(), 'DD-MM-YYYY_HH:mm:ss')}_${file.name}`
    };

    handleUpload(data);
  };

  const pdfValidation = (itm) =>
    itm.size > 6000000 && itm.type === 'application/pdf';

  const videoValidation = (itm) =>
    itm.size > 2000000000 && itm.type === 'video/mp4';

  const imageValidation = (itm) =>
    itm.size > 4000000 && itm.type === 'image/jpeg';

  const alertMessage = () => setVisible(true);

  const props = {
    multiple: false,
    accept: '.pdf,.jpg,.mp4',
    showUploadList: file,
    onRemove: () => {
      setFile(null);
    },
    beforeUpload: (element) => {
      setFile(null);
      if (pdfValidation(element)) {
        alertMessage();
      } else if (videoValidation(element)) {
        alertMessage();
      } else if (imageValidation(element)) {
        alertMessage();
      } else {
        setFile(element);
      }
      return false;
    },
    file
  };

  React.useEffect(() => {}, [options]);

  const onGetFileType = (type) => {
    switch (type) {
      case 'application/pdf':
        return 1; // pdf type id
      case 'image/jpeg':
        return 2; // image type id
      default:
        return 3; // video type id
    }
  };

  React.useEffect(() => {
    if (file) {
      form.setFieldsValue({
        elementType: onGetFileType(file.type)
      });
    }
  }, [file]);

  return (
    <StyledMainDiv
      className="h-full p-7 overflow-scroll"
      style={{ contain: 'strict' }}>
      <h1 className="text-base text-baxterGray font-bold mb-5">
        {translate('educationalMaterial.infoMaterial')}
      </h1>
      <Form
        className="material"
        form={form}
        {...layout}
        onFinish={onFinish}
        // onFinishFailed={onFinishFailed}
        validateMessages={validationMessages}>
        <Row gutter={20}>
          <Col xs={24} sm={12} md={12}>
            <Form.Item
              name="elementType"
              label={translate('educationalMaterial.elementType')}
              rules={[
                {
                  required: true,
                  message: translate('educationalMaterial.selecType')
                }
              ]}>
              <Select
                disabled
                // placeholder={translate('educationalMaterial.selecType')}
              >
                {educationElementType.map((itm, idx) => {
                  return (
                    <Option key={idx} value={itm.id}>
                      {translate(itm.desc)}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item
              name="title"
              label={translate('educationalMaterial.title')}
              rules={[
                {
                  required: true,
                  message: translate('educationalMaterial.typeTitle')
                }
              ]}>
              <Input />
            </Form.Item>
            <Form.Item name="description" label="Descripción">
              <Input.TextArea rows={5} showCount maxLength={250} />
            </Form.Item>
            <Form.Item
              name="stages"
              label={translate('educationalMaterial.stages')}
              rules={[
                {
                  required: true,
                  message: translate('educationalMaterial.selectStages')
                }
              ]}>
              <Checkbox.Group options={options} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={10} md={11}>
            <span className="text-base text-baxterGray">
              {translate('educationalMaterial.loadPrev')}
            </span>
            <div className="mt-2" />
            <Upload {...props} style={{ marginTop: 40 }} maxCount={1}>
              <StyledUpload>
                <p className="mt-10 mb-6">
                  <InboxOutlined
                    style={{
                      fontSize: 45,
                      color: 'var(--color-baxter-teal-medium)'
                    }}
                  />
                </p>
                <div className="mt-4" />
                <p className="text-base text-baxterGray font-bold">
                  {translate('educationalMaterial.click')}
                </p>
                <div className="mt-1" />
                <p className="text-baxterGray text-sm text-center">
                  {translate('educationalMaterial.restrictions')}
                </p>
              </StyledUpload>
            </Upload>
          </Col>
        </Row>
        <div className="flex  flex-col-reverse md:flex-row justify-end w-full mt-10">
          <Col xs={24} md={6}>
            <Form.Item>
              <StyledButton onClick={() => history.goBack()} type="link">
                {translate('commons.cancel')}
              </StyledButton>
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item>
              <StyledButton
                style={{ opacity: file ? 1 : 0.4 }}
                disabled={!file}
                icon={<UploadOutlined />}
                type="primary"
                htmlType="submit">
                {translate('dashboard.uploadMaterial')}
              </StyledButton>
            </Form.Item>
          </Col>
        </div>
      </Form>
      <Modal centered visible={visible} title="" closable={false} footer={null}>
        <div className="text-center pb-4 pt-8">
          <CloseCircleFilled
            style={{ color: 'var(--color-stage3b)', fontSize: 87 }}
          />
          <h2 className="mb-0 mt-6 text-2xl font-medium text-secondary">
            {translate('educationalMaterial.wrong')}
          </h2>
          <h2 className="mb-5 mt-2 text-sm text-gray-500">
            {translate('educationalMaterial.wrongText')}
          </h2>

          <div className="w-full flex flex-row justify-center mt-10">
            <CreateButton
              onClick={() => setVisible(false)}
              icon={<LeftOutlined style={{ color: 'white' }} />}>
              {translate('commons.goback')}
            </CreateButton>
          </div>
        </div>
      </Modal>
    </StyledMainDiv>
  );
}

/**
 * Component definition of proptypes.
 */
EducationalMaterialLoad.propTypes = {
  educationalElementActions: PropTypes.shape({
    createEducationalElement: PropTypes.func.isRequired,
    updateEducationalElement: PropTypes.func.isRequired
  }).isRequired,
  s3FileActions: PropTypes.shape({
    uploadS3File: PropTypes.func.isRequired
  }).isRequired
};

export default EducationalMaterialLoad;
